/**
 * Mapa kotev veřejného webu: JEDINÝ zdroj `id` sekcí a fragmentů odkazů na ně (řez 10,
 * F9.6 bod 5; od vize podnety-tymu-2026-10, řez 02, F5.1, česky a pro všechny kotvy webu).
 *
 * Web z této mapy bere `id` sekcí (`id={WEB_SECTIONS.trip.terminy}`), odkazy na ně
 * (`` `#${WEB_SECTIONS.trip.inquiry}` ``) a admin z ní skládá fragment adresy „Zobrazit
 * na webu“. Literál `id="…"` ani `href="#…"` v `apps/web/src` nepatří, protože by se kotva
 * a odkaz rozešly; hlídají to měřidla `scripts/kontrola/kotvy.mjs` (kotvy webu)
 * a `scripts/kontrola/admin-napovedy.mjs` (pravidla `kotva`, `kotva-zdroj`).
 *
 * Hodnoty jsou česky: `slugify` českého popisku záložky nebo nadpisu sekce, zkrácený na
 * podstatné slovo. Anglické hodnoty platné do řezu 02 (`ANGLICKE_KOTVY`) se nepřesměrovávají
 * (Ne-cíl vize): stará kotva `#africa` prostě nikam neskočí, a žádný alias `id` ji nesmí
 * vzkřísit. Klíče (vnitřní jména) se nemění, mění se jen hodnoty.
 *
 * Stránka hotelu a průvodce kotvy nemají: odkazy na ně vedou na stránku bez kotvy. Novou
 * kotvu přidává jen kontrakt řezu; tvar a jedinečnost hodnot hlídá `webSections.test.ts`.
 *
 * Směr importů: modul bere jen `types` a `utils`; `routes.ts` ho importuje (kotva
 * kontinentu), nikdy naopak.
 */
import { CONTINENT_LABELS, type Continent } from "./types";
import { slugify } from "./utils";

/**
 * Kotva sekce kontinentu (stránka destinací, video inspirace) a zároveň webová hodnota
 * parametru kontinentu ve slovníku parametrů (`webParams.ts`): `slugify` českého popisku.
 * Klíče vypsané ručně, ne iterací `CONTINENT_LABELS` (zobrazovací pořadí kontinentů hlídá
 * `continentOrderGuard.test.ts`, mapa ho nenese); úplnost vynucuje `Record<Continent, …>`.
 */
export const CONTINENT_SLUGS_CZ: Readonly<Record<Continent, string>> = {
  africa: slugify(CONTINENT_LABELS.africa),
  asia: slugify(CONTINENT_LABELS.asia),
  "south-america": slugify(CONTINENT_LABELS["south-america"]),
  "north-america": slugify(CONTINENT_LABELS["north-america"]),
  oceania: slugify(CONTINENT_LABELS.oceania),
  europe: slugify(CONTINENT_LABELS.europe),
};

export const WEB_SECTIONS = {
  destination: {
    popis: "popis",
    trips: "zajezdy",
    activities: "aktivity",
    weather: "pocasi",
    places: "kam-vyrazit",
    galerie: "galerie",
    tips: "tipy",
    nejlepsi: "nejlepsi",
    video: "video",
    oDestinaci: "o-destinaci",
  },
  trip: {
    description: "popis",
    map: "mapa",
    itinerary: "itinerar",
    included: "v-cene",
    excluded: "neni-v-cene",
    terminy: "terminy",
    weather: "pocasi",
    highlights: "co-zazijete",
    schedule: "co-vas-ceka",
    galerie: "galerie",
    podobne: "podobne",
    /**
     * Sekce poptávky detailu zájezdu (lišta záložek, tlačítko poptávky, mobilní lišta).
     * Ne `poptavka`: tu má jako `id` sekce formuláře stránka poptávky na míru.
     */
    inquiry: "poptavka-zajezdu",
  },
  inspiration: {
    popis: "popis",
    trips: "zajezdy",
    inspirations: "inspirace",
    gallery: "fotogalerie",
    videa: "videa",
    tips: "tipy",
    /** Předpona kotvy skupiny sekce zájezdů: `<předpona><skupina>` (`INSPIRATION_TRIP_GROUPS`). */
    tripsGroupPrefix: "zajezdy-",
  },
  /** Stránka charity: karty s kotvou na sekce téže stránky. */
  charity: {
    darovat: "darovat",
    dobrovolnictvi: "dobrovolnictvi",
    pomucky: "pomucky",
  },
  /**
   * Prvky layoutu na každé stránce. `obsah` = cíl odkazu „Přeskočit na obsah“; ne `obsah`
   * samotné, to vyrábí `addHeadingAnchors` (`apps/api/src/lib/richText.ts`) z nadpisu
   * „Obsah“ jednoduché stránky.
   */
  layout: {
    obsah: "hlavni-obsah",
  },
  /** Sekce kontinentů (stránka destinací, video inspirace). */
  continent: CONTINENT_SLUGS_CZ,
  hotel: {},
  guide: {},
} as const;

/**
 * Stránka webu, na kterou vede odkaz editoru adminu (klíč `WEB_SECTIONS`). Skupiny, které
 * stránkou editoru nejsou (prvky layoutu, kontinenty, charita), sem nepatří.
 */
export type WebPage = Exclude<keyof typeof WEB_SECTIONS, "layout" | "continent" | "charity">;

/**
 * Anglické kotvy, které web nesl do řezu 02 (zmrazeno 17. 10. 2026 z
 * `docs/vize/podnety-tymu-2026-10/nalezy.md` D a ze stránek adres). Žádná hodnota mapy
 * mezi nimi nesmí být a měřidla kotev je na stránkách hlásí jako nález (alias staré kotvy
 * by byl jejím přesměrováním, Ne-cíl vize). Seznam se nerozšiřuje o nové hodnoty, je to
 * snímek minulého stavu.
 */
export const ANGLICKE_KOTVY: readonly string[] = [
  // kontinenty enumu
  "asia",
  "africa",
  "south-america",
  "north-america",
  "oceania",
  "europe",
  // sekce detailů destinace, zájezdu a inspirace
  "trips",
  "weather",
  "places",
  "tips",
  "activities",
  "description",
  "map",
  "itinerary",
  "included",
  "excluded",
  "highlights",
  "schedule",
  "inquiry",
  "gallery",
  "inspirations",
  // charita
  "donate",
  "volunteer",
  "supplies",
  "share",
  // layout
  "main-content",
];
